import { useEffect, useState } from 'react';
import { App, Card, Flex, Tag, Tabs, Typography } from 'antd';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useGetPlans } from '@/features/agreements/hooks/usePlans';
import { useGetRuleGroups } from '@/features/agreements/hooks/useRuleGroups';
import { ratesQueryKeys } from '@/features/agreements/hooks/useRates';
import { getRates } from '@/features/agreements/services/ratesService';
import { SectionHeader } from '../SectionHeader';
import { LoadingIndicator } from '@/shared/components/LoadingIndicator';
import { ErrorMessage } from '@/shared/components/ErrorMessage';
import { EmptyStateCard } from '../EmptyStateCard';
import { AddRulesButton } from './AddRulesButton';
import { formatRuleValue } from './Utils/formatRuleValue';
import { PlanRatesTab, CreateRateButton } from './PlanRatesTab';
import { RuleGroupCard } from './RuleGroupCard';
import { ruleGroupsQueryKeys, useCreateRuleGroup, useUpdateRuleGroup } from '@/features/agreements/hooks/useRuleGroups';
import { RulesDrawer } from './RulesDrawer';

const { Text } = Typography;

export const RulesStep = ({ agreementId, onNextRef, readOnly = false }) => {
  const { notification } = App.useApp();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('agreement');
  const plansQuery = useGetPlans({ agreementId });
  const ruleGroupsQuery = useGetRuleGroups({ agreementId });
  const createRuleGroupMutation = useCreateRuleGroup();
  const updateRuleGroupMutation = useUpdateRuleGroup();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRuleGroup, setEditingRuleGroup] = useState(null);

  const plans = plansQuery.data ?? [];
  const hasPlans = plans.length > 0;
  const agreementRuleGroups = (ruleGroupsQuery.data ?? []).filter((ruleGroup) => ruleGroup.active); // Solo se muestra el grupo de reglas principal activo
  const hasRuleGroups = agreementRuleGroups.length > 0;

  // Obtener tasas de todos los planes para validación
  const allPlansRatesQueries = useQueries({
    queries: plans.map((plan) => ({
      queryKey: ratesQueryKeys.list({ agreementId, planId: plan.id }),
      queryFn: () => getRates(agreementId, plan.id),
      enabled: hasPlans,
    })),
  });

  const handleNext = async () => {
    if (readOnly) return;
    if (!hasPlans) {
      notification.error({
        message: <Text strong>Agrega al menos un plan</Text>,
        description: 'Es necesario crear al menos un plan para continuar con la configuración del convenio.',
      });
      return false;
    }

    // Verificar que cada plan tenga al menos una tasa configurada
    // Usar datos de queries si están disponibles, sino hacer llamadas
    const ratesPerPlan = allPlansRatesQueries.every((query) => query.data !== undefined)
      ? allPlansRatesQueries.map((query) => query.data ?? [])
      : await Promise.all(plans.map((plan) => getRates(agreementId, plan.id)));
    
    const allPlansHaveRates = ratesPerPlan.every((rates) => rates && rates.length > 0);
    
    if (!allPlansHaveRates) {
      const plansWithoutRates = plans.filter((plan, index) => !ratesPerPlan[index] || ratesPerPlan[index].length === 0);
      notification.error({
        message: <Text strong>Faltan tasas por configurar</Text>,
        description: `Los siguientes planes no tienen tasas configuradas: ${plansWithoutRates.map((p) => p.name).join(', ')}. Cada plan debe tener al menos una tasa para continuar.`,
      });
      return false;
    }
  };

  const getOperatorLabel = (operator) => {
    const operatorMap = {
      'eq': 'igual a',
      'neq': 'diferente de',
      'gt': 'mayor que',
      'gte': 'mayor o igual que',
      'lt': 'menor que',
      'lte': 'menor o igual que',
      'in': 'incluido en',
      'not_in': 'no incluido en',
      'between': 'entre',
      'contains': 'contiene',
    };
    return operatorMap[operator] || operator;
  };

  const getOperatorDescription = (operator) => {
    const operatorDescriptions = {
      'and': 'Se deben cumplir todas las condiciones',
      'or': 'Se debe cumplir al menos una condición',
      'not': 'Se niega la condición',
    };
    return operatorDescriptions[operator] || `Operador: ${operator}`;
  };

  useEffect(() => {
    if (!onNextRef) return;
    onNextRef.current = handleNext;
    return () => {
      onNextRef.current = null;
    };
  }, [handleNext, onNextRef, hasPlans, plans]);

  if (plansQuery.isLoading || ruleGroupsQuery.isLoading) return <LoadingIndicator />;

  if (plansQuery.isError) return <ErrorMessage error={plansQuery.error} />;
  if (ruleGroupsQuery.isError) return <ErrorMessage error={ruleGroupsQuery.error} />;

  // Función para invalidar queries de tasas cuando se crea una nueva
  const handleRateCreated = (planId) => {
    void queryClient.invalidateQueries({
      queryKey: ratesQueryKeys.list({ agreementId, planId }),
    });
  };

  // Determinar qué mostrar en tabBarExtraContent
  const getTabBarExtraContent = () => {
    if (readOnly) return null;

    if (activeTab === 'agreement') {
      // Si hay un ruleGroup activo, pasarlo para edición; si no, null para creación
      const activeRuleGroup = agreementRuleGroups.length > 0 ? agreementRuleGroups[0] : null;
      return <AddRulesButton agreementId={agreementId} ruleGroup={activeRuleGroup} />;
    }

    // Buscar el plan activo
    const activePlan = plans.find((plan) => plan.id === activeTab);
    if (!activePlan) {
      return null;
    }

    // Buscar el índice del plan en el array para obtener su query de tasas
    const planIndex = plans.findIndex((plan) => plan.id === activeTab);
    const ratesQuery = allPlansRatesQueries[planIndex];
    const hasRates = ratesQuery?.data && ratesQuery.data.length > 0;

    // Solo mostrar el botón si el plan tiene tasas
    if (hasRates) {
      return (
        <CreateRateButton
          agreementId={agreementId}
          planId={activePlan.id}
          onRateCreated={() => handleRateCreated(activePlan.id)}
        />
      );
    }

    return null;
  };

  // Construir items de tabs
  const tabItems = [
    {
      key: 'agreement',
      label: 'Condiciones del convenio',
      children: (
        <AgreementRulesTabContent
          ruleGroups={agreementRuleGroups}
          hasRuleGroups={hasRuleGroups}
          agreementId={agreementId}
          getOperatorLabel={getOperatorLabel}
          getOperatorDescription={getOperatorDescription}
          onEditRuleGroup={readOnly ? undefined : (ruleGroup) => {
            setEditingRuleGroup(ruleGroup);
            setDrawerOpen(true);
          }}
          readOnly={readOnly}
        />
      ),
    },
    ...plans.map((plan) => ({
      key: plan.id,
      label: plan.name,
      children: (
        <PlanRatesTab agreementId={agreementId} plan={plan} readOnly={readOnly} />
      ),
    })),
  ];

  return (
    <Flex justify='center' style={{ paddingInline: '5%' }}>
      <Flex vertical gap='large' flex={1} style={{ width: '100%' }}>
        <SectionHeader
          title='Condiciones y restricciones'
          description='Define cuándo este convenio puede o no aplicarse durante una cotización. Además, crear reglas para aplicar tasas de acuerdo a cada plan del convenio.'
        />
        <Tabs
          items={tabItems}
          defaultActiveKey='agreement'
          activeKey={activeTab}
          onChange={setActiveTab}
          tabBarExtraContent={getTabBarExtraContent()}
        />
        <RulesDrawer
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            setEditingRuleGroup(null);
          }}
          title='Definir reglas del convenio'
          description='Las reglas determinan cuándo el convenio puede ser aplicado durante una cotización. Todas las condiciones definidas deben cumplirse según la lógica configurada.'
          ruleGroup={editingRuleGroup}
          onSave={async ({ ruleGroupPayload, ruleGroupId }) => {
            if (ruleGroupId != null) {
              await updateRuleGroupMutation.mutateAsync({
                id: ruleGroupId,
                data: ruleGroupPayload,
              });
              notification.success({
                message: <Text strong>Reglas actualizadas</Text>,
                description: '¡Las reglas del convenio se han actualizado correctamente!',
              });
            } else {
              await createRuleGroupMutation.mutateAsync({
                data: { ...ruleGroupPayload, agreementId },
              });
              notification.success({
                message: <Text strong>Reglas creadas</Text>,
                description: '¡Las reglas del convenio se han creado correctamente!',
              });
            }
            void queryClient.invalidateQueries({
              queryKey: ruleGroupsQueryKeys.list({ agreementId }),
            });
          }}
          submitButtonText={editingRuleGroup ? 'Guardar' : 'Crear condición'}
          rulesRequired={true}
        />
      </Flex>
    </Flex>
  );
};

const AgreementRulesTabContent = ({
  ruleGroups,
  hasRuleGroups,
  agreementId,
  getOperatorLabel,
  getOperatorDescription,
  onEditRuleGroup,
  readOnly = false,
}) => {
  const queryClient = useQueryClient();
  if (!hasRuleGroups) {
    return (
      <EmptyStateCard
        title='Este convenio no tiene condiciones generales definidas'
        description='Puedes usar condiciones para limitar el uso del convenio según tipo de vehículo, antigüedad, marca u otros criterios.'
        action={!readOnly ? <AddRulesButton agreementId={agreementId} ruleGroup={null} /> : null}
      />
    );
  }

  return (
    <Flex vertical gap='middle'>
      {ruleGroups.map((ruleGroup) => (
        <RuleGroupCard
          key={ruleGroup.id}
          ruleGroup={ruleGroup}
          level={0}
          parentActive={true}
          getOperatorLabel={getOperatorLabel}
          getOperatorDescription={getOperatorDescription}
          agreementId={agreementId}
          onChange={() => {
            void queryClient.invalidateQueries({
              queryKey: ruleGroupsQueryKeys.list({ agreementId }),
            });
          }}
          onEdit={() => onEditRuleGroup?.(ruleGroup)}
          showRuleActions={!readOnly}
          showGroupActions={!readOnly}
        />
      ))}
    </Flex>
  );
};

